import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import { IUser } from "app/auth/user.model";
import { Events, NavController, NavParams } from "ionic-angular";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import {
  COMPANY_NAVIGATION,
  QA_QUEUE,
} from "../../../app/access-control/common-permission-asks";
import {
  Locations,
  QaQueue,
  Workforce,
} from "../../../app/access-control/permissions";
import { PermissionsResponse } from "../../../app/access-control/permissions-response";
import { NavigationService } from "../../../navigation/navigation.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

@Component({
  selector: "company-navigation",
  templateUrl: "company-navigation.html",
})
export class CompanyNavigation implements OnInit, OnDestroy {
  @Output()
  navigated = new EventEmitter<string>();

  private _activePage: string = "CompanyManagementPage";
  private _companyManagementEnabled: boolean = false;
  private _rolesEnabled: boolean = false;
  private _locationEnabled: boolean = true;
  private _workforceEnabled: boolean = true;
  subscriptions: Subscription[] = [];
  user: IUser;
  private _qaControlsEnabled: boolean = false;
  private _reportsEnabled: boolean = true;
  qaQueueToggle: boolean = false;
  hasWorkforceAccess: boolean = false;
  hasLocationAccess: boolean = false;
  hasWorkforceAccessAll: boolean = false;
  hasLocationAccessAll: boolean = false;
  permissionsAccess = {} as PermissionsResponse;

  // Kept as a stable reference so ngOnDestroy can unsubscribe THIS handler only.
  // Events.unsubscribe(topic) with no handler deletes the whole topic channel,
  // which would also drop other components' handlers (e.g. UserSettingsNavigation).
  private readonly onOrganizationsUpdated = (user: IUser) => {
    this.user = user;
    this.companyManagementEnabled = this.showCompanyManagement();
    this.rolesEnabled = this.showRolesOrLocation();
  };

  constructor(
    public navCtrl: NavController,
    private navigationService: NavigationService,
    public navParams: NavParams,
    private authSvc: AuthService,
    public stateData: StateManagementService,
    private accessControlService: AccessControlService,
    public events: Events
  ) {
    this.events.subscribe(
      "user:updateorganizations",
      this.onOrganizationsUpdated
    );
  }

  ngOnInit(): void {
    this.user = this.authSvc.getUser();
    this.companyManagementEnabled = this.showCompanyManagement();
    this.rolesEnabled = this.showRolesOrLocation();

    this.subscriptions.push(
      this.accessControlService
        .getPermission(COMPANY_NAVIGATION.concat(QA_QUEUE))
        .subscribe((data) => {
          this.permissionsAccess = data;
          this.checkCompanyNavigationPermissions();
          this.checkPagesPermissions();
        })
    );
    this.subscriptions.push(
      this.stateData.qaQueueToggle.subscribe((toggle) => {
        this.qaQueueToggle = !!toggle;
        this._qaControlsEnabled = this.qaQueueToggle;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((val) => val.unsubscribe());
    this.events.unsubscribe(
      "user:updateorganizations",
      this.onOrganizationsUpdated
    );
  }

  ionViewDidLoad() {}

  showCompanyManagement(): boolean {
    if (this.user) {
      return (
        this.user.selectedOrgType === "COMPANY" &&
        this.user.organizations.filter((o) => o.orgType === "COMPANY").length >
          1
      );
    } else {
      return false;
    }
  }

  showRolesOrLocation(): boolean {
    if (this.user) {
      return (
        this.user.selectedOrgType === "COMPANY" &&
        this.user.organizations.filter((o) => o.orgType === "COMPANY").length >
          0
      );
    } else {
      return false;
    }
  }

  redirectPage(entity: string) {
    let page = "";

    switch (entity) {
      case "location":
        if (!this.showRolesOrLocation()) {
          page = "LocationGroup";
        } else {
          page = "CompanyDetailsPage";
        }
        break;
      case "workforce":
        page = "WorkforcePage";
        break;
      case "roles":
        page = "RolesPage";
        break;
      case "qa-controls":
        page = "QaControlsPage";
        break;
      case "reports":
        page = "ResourceReportsPage";
        break;
      case "billingSettings":
        page = "BillingSettingsPage";
        break;
      default:
        page = "CompanyManagementPage";
    }

    this.navigationService.goToInternalPage(page, this.navParams);
  }

  @Input()
  set activePage(value: string) {
    this._activePage = value;
  }

  // @Input()
  set companyManagementEnabled(value: boolean) {
    this._companyManagementEnabled = value;
  }

  // @Input()
  set locationEnabled(value: boolean) {
    this._locationEnabled = value;
  }

  // @Input()
  set workforceEnabled(value: boolean) {
    this._workforceEnabled = value;
  }

  // @Input()
  set rolesEnabled(value: boolean) {
    this._rolesEnabled = value;
  }

  @Input()
  set qaControlsEnabled(value: boolean) {
    // this._qaControlsEnabled = !!value; // after MAV-2337 is verified in PROD, remove the @Input() since other components are sending 'true'
  }

  @Input() set reportsEnabled(value: boolean) {
    this._reportsEnabled = value;
  }

  get activePage(): string {
    return this._activePage;
  }

  get companyManagementEnabled(): boolean {
    return this._companyManagementEnabled;
  }

  get locationEnabled(): boolean {
    return this._locationEnabled;
  }

  get workforceEnabled(): boolean {
    return this._workforceEnabled;
  }

  get rolesEnabled(): boolean {
    return this._rolesEnabled;
  }

  get qaControlsEnabled(): boolean {
    return this._qaControlsEnabled;
  }

  get reportsEnabled(): boolean {
    return this._reportsEnabled && this.user
      ? this.user.selectedOrgType === "LOCATION"
      : false;
  }

  checkCompanyNavigationPermissions() {
    this.hasWorkforceAccess = this.accessControlService.hasAccess(
      this.permissionsAccess,
      Workforce.CompanyWorkforceView
    );
    this.hasLocationAccess = this.accessControlService.hasAccess(
      this.permissionsAccess,
      Locations.CompanyLocationsView
    );
    this.hasWorkforceAccessAll = this.accessControlService.hasAccess(
      this.permissionsAccess,
      Workforce.CompanyWorkforceAll
    );
    this.hasLocationAccessAll = this.accessControlService.hasAccess(
      this.permissionsAccess,
      Locations.CompanyLocationsAll
    );
  }

  checkPagesPermissions(): void {
    if (
      this._activePage === "WorkForcePage" &&
      !this.hasWorkforceAccess &&
      !this.hasWorkforceAccessAll
    ) {
      this.navigationService.goToInternalPage("AccessDeniedPage", {});
    }
    if (
      this._activePage === "LocationPage" &&
      !this.hasLocationAccess &&
      !this.hasLocationAccessAll
    ) {
      this.navigationService.goToInternalPage("AccessDeniedPage", {});
    }
  }

  get showQaQueueButton() {
    if (!!this.qaQueueToggle === true) {
      return this.accessControlService.hasAccess(
        this.permissionsAccess,
        QaQueue.CompanyControlsView
      );
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/company-navigation/company-navigation.ts